import type {Density} from './types'

export const columnGap = (el: Element | null) => {
  if (!el) return 0
  const computedStyle = window.getComputedStyle(el)
  if (computedStyle?.columnGap) return parseInt(computedStyle.columnGap, 10) // e.g., '16px' => 16
  return 0
}

const calculateItemOffsetWidth = (item: HTMLElement, gap: number) => {
  const itemStyle = window.getComputedStyle(item)
  const marginLeft = parseInt(itemStyle.marginLeft, 10) || 0
  const marginRight = parseInt(itemStyle.marginRight, 10) || 0
  return item.offsetWidth + marginLeft + marginRight + gap * 2
}

/**
 * Calculate the offset width of a single item within its container.
 * Handles gap calculation internally.
 */
export const getItemWidth = (item: HTMLElement, container: HTMLElement): number => {
  const gap = columnGap(container)
  return calculateItemOffsetWidth(item, gap)
}

/**
 * Batch calculate offset widths for multiple items within their container.
 * Batches all layout reads together to minimize forced synchronous layout.
 * @param items Array of {key, el} pairs to measure
 * @param container The flex container to read gap from
 * @returns Map of key to calculated width
 */
export const batchGetItemWidths = (
  items: Array<{key: string; el: HTMLElement}>,
  container: HTMLElement,
): Map<string, number | undefined> => {
  const widths = new Map<string, number | undefined>()
  if (items.length === 0) return widths

  // Read gap once for all items
  const gap = columnGap(container)

  // Batch all offsetWidth reads together
  for (const {key, el} of items) {
    widths.set(key, calculateItemOffsetWidth(el, gap))
  }

  return widths
}

/**
 * How much space is the overflow menu toggle button taking up?
 */
export const calculateMenuSpace = (outerContainer: HTMLDivElement | null, itemContainer: HTMLDivElement | null) => {
  const availableSpace = calculateAvailableSpace(outerContainer, itemContainer)
  if (typeof availableSpace === 'undefined') return
  let menuSpace = availableSpace
  if (itemContainer) menuSpace -= itemContainer.offsetLeft
  return menuSpace
}

/**
 * How much space do we have to expand into, to show more action items?
 * @returns Get the offset of the item container from the container edge
 */
export const calculateAvailableSpace = (
  outerContainer: HTMLDivElement | null,
  itemContainer: HTMLDivElement | null,
) => {
  if (!outerContainer) return
  let availableSpace = outerContainer.offsetWidth
  if (itemContainer) availableSpace -= itemContainer.offsetWidth
  return availableSpace
}

export const gapFromDensity = (density?: Density) => {
  if (density === 'condensed') return 'var(--base-size-4)'
  if (density === 'spacious') return 'var(--base-size-16)'
  if (density === 'none') return 0
  return 'var(--base-size-8)'
}
